import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCASamples, getCASampleAnnualStatus, createCASample, archiveCASample,
} from "../../api/client";
import DocumentsPanel from "../Documents/DocumentsPanel";
import { CASample, CASampleAnnualRow } from "../../types";

/**
 * Control Authority IU sampling events (40 CFR 403.8(f)(2)(v) — sample each SIU at
 * least once a year). Parallels InspectionsPanel: annual-requirement roster, open
 * re-sample follow-ups, and the sampling log with an add form.
 *
 * A completed CA sampling event counts toward the annual requirement regardless of
 * outcome; an "exceedance" result opens a re-sample follow-up that a compliant
 * re-sample resolves.
 */
const today = () => new Date().toISOString().slice(0, 10);

const CASamplingPanel: React.FC = () => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [status, setStatus] = useState<{ total_ius: number; ca_sampled: number; not_ca_sampled: number;
    industrial_users: CASampleAnnualRow[] } | null>(null);
  const [log, setLog] = useState<CASample[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // The entry opened to show its details and its lab report.
  const [openId, setOpenId] = useState<number | null>(null);
  const [form, setForm] = useState({
    company_id: "", sample_date: today(), result: "compliant",
    collector_name: "", parameters: "", notes: "", in_lieu_of_iu: false, parent_ca_sample_id: "",
  });

  const load = useCallback(() => {
    getCASampleAnnualStatus(year).then(r => setStatus(r.data)).catch(() => {});
    getCASamples({ year }).then(r => setLog(r.data)).catch(() => {});
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const openFollowups = useMemo(() => log.filter(c => c.follow_up_open), [log]);

  const submit = async () => {
    setError("");
    if (!form.company_id) { setError("Choose an industrial user."); return; }
    if (!form.sample_date) { setError("Sample date is required."); return; }
    setSaving(true);
    try {
      const created = await createCASample({
        company_id: Number(form.company_id),
        sample_date: form.sample_date,
        result: form.result,
        collector_name: form.collector_name || undefined,
        parameters: form.parameters || undefined,
        notes: form.notes || undefined,
        in_lieu_of_iu: form.in_lieu_of_iu,
        parent_ca_sample_id: form.parent_ca_sample_id ? Number(form.parent_ca_sample_id) : undefined,
      });
      setShowForm(false);
      setForm(f => ({ ...f, collector_name: "", parameters: "", notes: "", result: "compliant",
        in_lieu_of_iu: false, parent_ca_sample_id: "", sample_date: today() }));
      // Open the new entry so its lab report can be attached straight away.
      if (created?.data?.id) setOpenId(created.data.id);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Could not save CA sample.");
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async (c: CASample) => {
    if (!window.confirm(`Archive the ${c.sample_date} CA sample of ${c.company_name}? It stays on record but is hidden.`)) return;
    try { await archiveCASample(c.id); load(); }
    catch (e: any) { setError(e?.response?.data?.error ?? "Could not archive."); }
  };

  const startResample = (parent: CASample) => {
    setForm(f => ({ ...f, company_id: String(parent.company_id),
      parent_ca_sample_id: String(parent.id), result: "compliant", sample_date: today() }));
    setShowForm(true);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18 }}>CA Sampling</h3>
          <p style={{ margin: "2px 0 0", color: "#718096", fontSize: 13 }}>
            The CA must sample each significant IU at least once per calendar year (40 CFR 403.8(f)(2)(v)).
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
            {[0, 1, 2].map(d => new Date().getFullYear() - d).map(y =>
              <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={btn} onClick={() => setShowForm(v => !v)}>
            {showForm ? "Close" : "+ Log CA sample"}
          </button>
        </div>
      </div>

      {error && <p style={{ color: "#c53030", fontSize: 13 }}>{error}</p>}

      {status && (
        <div style={card}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <Stat label="Industrial users" value={status.total_ius} />
            <Stat label="CA-sampled this year" value={status.ca_sampled} color="#2f855a" />
            <Stat label="Not yet CA-sampled" value={status.not_ca_sampled}
                  color={status.not_ca_sampled > 0 ? "#c53030" : "#2f855a"} />
            <Stat label="Open re-samples" value={openFollowups.length}
                  color={openFollowups.length > 0 ? "#c05621" : "#2f855a"} />
          </div>
          {status.not_ca_sampled > 0 && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "#c53030" }}>
              {status.industrial_users.filter(r => !r.ca_sampled_this_year).map(r => r.name).join(", ")}
              {" "}— not yet CA-sampled in {year}.
            </p>
          )}
        </div>
      )}

      {showForm && (
        <div style={card}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={field}><span style={lbl}>Industrial user</span>
              <select style={inp} value={form.company_id}
                onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}>
                <option value="">— select —</option>
                {(status?.industrial_users ?? []).map(r =>
                  <option key={r.company_id} value={r.company_id}>{r.name}</option>)}
              </select>
            </label>
            <label style={field}><span style={lbl}>Date</span>
              <input type="date" style={inp} value={form.sample_date}
                onChange={e => setForm(f => ({ ...f, sample_date: e.target.value }))} />
            </label>
            <label style={field}><span style={lbl}>Result</span>
              <select style={inp} value={form.result}
                onChange={e => setForm(f => ({ ...f, result: e.target.value }))}>
                <option value="compliant">Compliant</option>
                <option value="exceedance">Exceedance</option>
              </select>
            </label>
            <label style={field}><span style={lbl}>Collected by</span>
              <input style={inp} value={form.collector_name}
                onChange={e => setForm(f => ({ ...f, collector_name: e.target.value }))} />
            </label>
            <label style={field}><span style={lbl}>Parameters</span>
              <input style={inp} value={form.parameters} placeholder="e.g. BOD, TSS, Zn"
                onChange={e => setForm(f => ({ ...f, parameters: e.target.value }))} />
            </label>
            <label style={{ ...field, justifyContent: "flex-end" }}>
              <span style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={form.in_lieu_of_iu}
                  onChange={e => setForm(f => ({ ...f, in_lieu_of_iu: e.target.checked }))} />
                CA samples in lieu of IU self-monitoring
              </span>
            </label>
            <label style={{ ...field, gridColumn: "1 / -1" }}><span style={lbl}>Notes</span>
              <textarea style={{ ...inp, minHeight: 56 }} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </label>
          </div>
          {form.parent_ca_sample_id &&
            <p style={{ fontSize: 12, color: "#c05621", margin: "8px 0 0" }}>
              Logging a re-sample of #{form.parent_ca_sample_id} — a compliant result clears its follow-up.
            </p>}
          {form.result === "exceedance" &&
            <p style={{ fontSize: 12, color: "#c05621", margin: "8px 0 0" }}>
              An exceedance will open a re-sample follow-up (still counts as CA-sampled this year).
            </p>}
          <div style={{ marginTop: 12 }}>
            <button style={btn} onClick={submit} disabled={saving}>
              {saving ? "Saving…" : "Save CA sample"}
            </button>
          </div>
        </div>
      )}

      {openFollowups.length > 0 && (
        <div style={{ ...card, borderColor: "#f6ad55" }}>
          <strong style={{ fontSize: 14 }}>Open re-samples ({openFollowups.length})</strong>
          {openFollowups.map(c => (
            <div key={c.id} style={row}>
              <span><strong>{c.company_name}</strong>
                <span style={meta}> · {c.sample_date} · exceedance
                  {c.parameters ? ` (${c.parameters})` : ""}</span></span>
              <button style={outline} onClick={() => startResample(c)}>Log re-sample</button>
            </div>
          ))}
        </div>
      )}

      <div style={card}>
        <strong style={{ fontSize: 14 }}>CA sampling log — {year}</strong>
        {log.length === 0
          ? <p style={{ color: "#718096", fontSize: 13, margin: "6px 0 0" }}>No CA samples recorded for {year}.</p>
          : log.map(c => (
            <React.Fragment key={c.id}>
            <div style={row}>
              <button type="button" style={rowBtn} aria-expanded={openId === c.id}
                title="Show the details and lab report" onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                <span style={meta}>{openId === c.id ? "▾ " : "▸ "}</span>
                <strong>{c.company_name}</strong>
                <span style={meta}> · {c.sample_date} · </span>
                <span style={{ color: c.result === "exceedance" ? "#c53030" : "#2f855a", fontWeight: 600 }}>
                  {c.result === "exceedance" ? "Exceedance" : "Compliant"}
                </span>
                {c.parameters && <span style={meta}> · {c.parameters}</span>}
                {c.in_lieu_of_iu && <span style={meta}> · in lieu of IU</span>}
                {c.follow_up_open && <span style={{ color: "#c05621" }}> · re-sample open</span>}
                {c.parent_ca_sample_id && <span style={meta}> · re-sample of #{c.parent_ca_sample_id}</span>}
              </button>
              <button style={outline} onClick={() => onArchive(c)}>Archive</button>
            </div>
            {openId === c.id && (
              <div style={detail}>
                <div style={dl}>
                  <span style={lbl}>Industrial user</span><span>{c.company_name}</span>
                  <span style={lbl}>Date</span><span>{c.sample_date}</span>
                  <span style={lbl}>Result</span><span>{c.result === "exceedance" ? "Exceedance" : "Compliant"}</span>
                  <span style={lbl}>Parameters</span><span>{c.parameters || "—"}</span>
                  <span style={lbl}>Collected by</span><span>{c.collector_name || "—"}</span>
                  <span style={lbl}>In lieu of the IU</span><span>{c.in_lieu_of_iu ? "Yes" : "No"}</span>
                  <span style={lbl}>Notes</span>
                  <span style={{ whiteSpace: "pre-wrap" }}>{c.notes || "None recorded."}</span>
                  {c.follow_up_required && (<>
                    <span style={lbl}>Re-sample</span>
                    <span>{c.follow_up_open ? "Open — log a re-sample to close it"
                      : `Resolved ${c.resolved_at ? c.resolved_at.slice(0, 10) : ""}`}</span>
                  </>)}
                  {c.parent_ca_sample_id && (<>
                    <span style={lbl}>Re-sample of</span><span>sample #{c.parent_ca_sample_id}</span>
                  </>)}
                  <span style={lbl}>Logged by</span>
                  <span>{c.created_by_name || "—"}{c.created_at ? ` · ${c.created_at.slice(0, 10)}` : ""}</span>
                </div>
                <DocumentsPanel entityType="ca_sample" entityId={c.id} category="lab_report"
                  title="Lab report" emptyText="No lab report attached yet — upload the laboratory's report (PDF)." />
              </div>
            )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 24, fontWeight: 700, color: color ?? "#2d3748" }}>{value}</div>
    <div style={{ fontSize: 12, color: "#718096" }}>{label}</div>
  </div>
);

const card: React.CSSProperties = { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 14 };
const row: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center",
  gap: 8, fontSize: 13, padding: "7px 0", borderBottom: "1px solid #eee" };
const detail: React.CSSProperties = { background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 6,
  padding: "10px 12px", margin: "4px 0 10px", fontSize: 13 };
const dl: React.CSSProperties = { display: "grid", gridTemplateColumns: "140px minmax(0, 1fr)", gap: "4px 12px",
  margin: "0 0 10px" };
const rowBtn: React.CSSProperties = { minWidth: 0, flex: 1, background: "none", border: "none", padding: 0,
  textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" };
const meta: React.CSSProperties = { color: "#718096" };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#4a5568" };
const inp: React.CSSProperties = { padding: "7px 9px", border: "1px solid #cbd5e0", borderRadius: 6, fontSize: 14 };
const sel: React.CSSProperties = { ...inp, padding: "6px 8px" };
const btn: React.CSSProperties = { background: "#3182ce", color: "#fff", border: "none", borderRadius: 6,
  padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const outline: React.CSSProperties = { background: "#fff", color: "#4a5568", border: "1px solid #cbd5e0",
  borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer" };

export default CASamplingPanel;
